import { useEffect, useState } from "react";
import AppShell, { type AppInfo } from "./components/AppShell";
import Composer from "./components/Composer";
import { useAuth } from "./contexts/AuthProvider";
import { useAgent } from "./hooks/useAgent";
import { usePlugins } from "./hooks/usePlugins";
import { useHosts } from "./hooks/useHosts";
import { describeAgent } from "./lib/agentClient";
import { isConfigured, isLocalHost } from "./lib/config";
import Login from "./pages/Login";
import UpdatePassword from "./pages/UpdatePassword";
import AuthConfirm from "./pages/AuthConfirm";
import { AUTH_CONFIRM_PATH } from "./lib/authConfirm";
import { useOrganizations } from "./components/OrgSwitcher";
import { OrgGateView } from "./components/OrgGateView";
import { InterruptPanel } from "./components/chat/InterruptPanel";
import { ActivePlanPanel } from "./components/chat/ActivePlanPanel";
import { orgGate } from "./lib/orgGate";
import { DEFAULT_ROUTE, resolveRoute, routePath } from "./routes";
import type { AgentTarget } from "./lib/agentClient";
import "./ui/brand.css";

/**
 * The gates in front of the shell, in the order they can be answered.
 *
 * Each one is a genuinely reachable state, and the order is not cosmetic: the
 * page is served from a public hostname, so it *will* be opened in an ordinary
 * browser; it deploys separately from the desktop (hard rule 6), so it *will*
 * meet installed versions older than itself; and a build can be unconfigured.
 * Every branch renders inside `Frame`, so the agent line is visible from all of
 * them — the shell's instance card takes over once we are past them.
 */

/**
 * Bump when the renderer starts *requiring* a channel added in a later bridge.
 *
 * Still 1, deliberately, even though this build uses the v2 `agent` channel: it
 * feature-detects and says so when the channel is missing, which is the whole
 * point of having a version. A floor is for what the app cannot do without.
 */
const MIN_BRIDGE_VERSION = 1;

/** The sign-in URL. Not one of the `ROUTES` — the shell never renders it; it is
 *  the address the auth guard parks a signed-out user at. */
const LOGIN_PATH = "/login";

/** sessionStorage key: the protected URL a signed-out user was headed for, so a
 *  successful sign-in returns them there instead of the default route. Mirrors
 *  bimgo-ai-web's `?redirect=` handling, adapted to this router-less SPA. */
const POST_LOGIN_REDIRECT_KEY = "post_login_redirect";

export default function App() {
  const { session, ready, signOut } = useAuth();
  const agent = useAgent();
  const smokeShell = import.meta.env.DEV && isLocalHost() &&
    new URLSearchParams(window.location.search).has("smoke-shell");
  // Start alongside the organization lookup, while its loading gate is still on screen.
  const checkConnections = (ready && session !== null) || smokeShell;
  const plugins = usePlugins(checkConnections);
  const hosts = useHosts(checkConnections);
  /**
   * Hoisted out of `AppShell` so the organization check can be one of the gates
   * below rather than something the shell discovers after it has already drawn
   * itself (#63). It fetches nothing until there is a user, so the calls above
   * this line are unaffected; the shell takes the result as a prop, which also
   * keeps it to a single fetch.
   */
  const organizations = useOrganizations(
    session?.user?.id ?? null,
    session?.access_token ?? null,
  );
  /**
   * Which organization the app is working in. Held here rather than in
   * `AppShell` because the gate below *replaces* the shell — so a user blocked
   * on one org has to be able to switch from a page where the shell, and its
   * account menu, do not exist.
   */
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);

  const [info, setInfo] = useState<AppInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Feature-detect the exact call, not just `window.bimgo`.
    if (!window.bimgo?.app?.info) return;
    window.bimgo.app
      .info()
      .then(setInfo)
      .catch((e: unknown) => setError(String(e)));
  }, []);

  // Auth-guard routing. Keep the URL honest about who is signed in:
  //   - a signed-out user on a protected route is *redirected* to /login, and
  //     where they were headed is remembered so sign-in returns them there;
  //   - a signed-in user who lands on /login is bounced on to that destination
  //     (or the default route).
  // Without this the login form was rendered *in place* — the address bar still
  // read /projects while showing sign-in, and after login you never returned to
  // where you meant to go.
  //
  // Runs only where this instance actually owns the sign-in screen — a
  // configured desktop build past the bridge gate — so it never fights the
  // "open me in the app" / "not configured" gates below. /update-password is left
  // alone: a recovery link lands there before a session exists (item J).
  useEffect(() => {
    if (!ready) return;

    const ownsAuthScreen = Boolean(window.bimgo) && isConfigured();
    if (!ownsAuthScreen) return;

    const { pathname, search, hash } = window.location;
    const onLogin = pathname === LOGIN_PATH;
    const onRecovery = resolveRoute(pathname) === "update-password" || pathname === AUTH_CONFIRM_PATH;

    // replaceState (not push) so Back never returns to a page the current auth
    // state can't show; the dispatched popstate resyncs useRoute, which pushState
    // and replaceState do not fire on their own.
    const redirect = (url: string): void => {
      window.history.replaceState(null, "", url);
      window.dispatchEvent(new PopStateEvent("popstate"));
    };

    if (!session) {
      if (!onLogin && !onRecovery) {
        sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, `${pathname}${search}${hash}`);
        redirect(`${LOGIN_PATH}${search}`);
      }
      return;
    }

    if (onLogin) {
      const saved = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
      sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
      // The default fallback keeps the current query string, exactly as
      // useRoute.navigate does. A saved destination already carries its own search.
      const target =
        saved && !saved.startsWith(LOGIN_PATH) ? saved : `${routePath(DEFAULT_ROUTE)}${search}`;
      redirect(target);
    }
  }, [ready, session]);

  /**
   * Dev-only shell fixture, and it is a fixture rather than a feature.
   *
   * The shell is behind login, and login is blocked on credentials only the
   * owner can supply (item J). Rather than leave the layout and the routing
   * unverified until then, `?smoke-shell=1` mounts the **real** `AppShell` with
   * no session — real sidebar, real router, real panes, real instance card. What it
   * cannot show is the org name, because that needs a token; that clause of
   * item L's Verify stays with item J.
   *
   * `import.meta.env.DEV` is a build-time constant, so this is absent from
   * anything shipped.
   */
  if (smokeShell) {
    return (
      <AppShell
        session={null}
        agent={agent}
        info={info}
        plugins={plugins}
        hosts={hosts}
        organizations={organizations}
        selectedOrgId={selectedOrgId}
        onSelectOrg={setSelectedOrgId}
      />
    );
  }

  /**
   * Dev-only page preview, so the login page can be seen in an ordinary browser
   * (`npm run dev`) without going through the desktop-bridge gate. `?preview=login`
   * renders the sign-in page as served, stripped from shipped builds by
   * `import.meta.env.DEV`.
   *
   * The whole shell (chat as the default route) is `?smoke-shell=1`.
   */
  if (import.meta.env.DEV && isLocalHost()) {
    const preview = new URLSearchParams(window.location.search).get("preview");
    if (preview === "login") return <Login />;
    /**
     * The password-reset confirmation (#72): the only other way to see it is to
     * spend a real recovery link, so its two actions — open the desktop, or
     * install it — were reviewable only by argument. `?preview=password-done`.
     */
    if (preview === "password-done") return <UpdatePassword previewDone />;
    /**
     * The two blocked pages, without needing an organization whose subscription
     * has actually lapsed (#63). Reproducing those states against a real backend
     * means editing a subscription row, so without this they were reviewable
     * only by argument — which is how the first cut of this gate shipped
     * rendering inside the shell, with the rail and nav still on screen, and
     * nobody saw it until it was running.
     *
     * `?preview=expired` and `?preview=no-org`. Dev-only, like the two above.
     */
    /**
     * Plan mode's `ask_user` card (#99), with the payload from the ticket's trace: a
     * question, four options, single-select — and the middleware's `description` that
     * the first card mistook for the question. Reaching it for real takes a plan-mode
     * turn against a live agent and a Revit that needs clarifying, so it was reviewable
     * only by argument. `?preview=ask-user` (single) and `?preview=ask-user-multi`.
     */
    if (preview === "ask-user" || preview === "ask-user-multi") {
      const askUser = {
        name: "ask_user",
        args: {
          question: "匯出目前圖紙為 CAD 時，圖層要依什麼分類？",
          options: ["依 Revit 類別", "依視圖中的可見性", "全部放同一層", "依線寬"],
          allowMultiple: preview === "ask-user-multi",
        },
        description:
          "Review this plan-mode question before continuing.\nTool: ask_user\nArgs: {\"question\":\"…\",\"options\":[\"…\"]}",
      };
      return (
        <div style={{ padding: "1.5rem", maxWidth: "44rem" }}>
          <InterruptPanel
            interrupt={{ thread_id: "preview", action_requests: [askUser], review_configs: [] }}
            busy={false}
            onRespond={(decision) => console.log("[preview] resume", JSON.stringify(decision))}
          />
        </div>
      );
    }
    /**
     * The approved plan's floating button and modal, inside a scrolling stand-in for the
     * transcript so the sticky wrapper can be seen pinning it to the bottom edge.
     * `?preview=active-plan`.
     */
    if (preview === "active-plan") {
      const plan = {
        title: "匯出圖紙為 CAD 並分層",
        plan:
          "## 目標\n將目前圖紙匯出為 DWG，圖層依 Revit 類別分類。\n\n## 步驟\n1. 讀取目前視圖的可見類別\n2. 建立匯出設定：每個類別一層，線寬依類別\n3. 匯出到指定資料夾\n\n## 成功條件\n- 每個類別都有對應圖層\n- 註解圖層保留",
      };
      return (
        <div className="chat-scroll" style={{ height: "60vh", border: "1px solid #e5e7eb", margin: "1.5rem", maxWidth: "44rem" }}>
          <div className="chat-thread" style={{ padding: "1rem" }}>
            {Array.from({ length: 30 }, (_, i) => (
              <p key={i} style={{ color: "#6b7280" }}>
                transcript line {i + 1}
              </p>
            ))}
            <ActivePlanPanel plan={plan} onDismiss={() => console.log("[preview] dismiss plan")} />
          </div>
        </div>
      );
    }
    if (preview === "expired" || preview === "no-org") {
      const fixture = [
        { id: "lapsed", name: "Lapsed Organization", active_subscription: null },
        { id: "valid", name: "Another Organization", active_subscription: { plan_name: "Pro" } },
      ];
      return (
        <OrgGateView
          gate={
            preview === "expired"
              ? { kind: "subscription-expired", organizationName: "Lapsed Organization" }
              : { kind: "no-organization" }
          }
          organizations={preview === "expired" ? fixture : []}
          currentOrgId="lapsed"
          onSelectOrg={() => {}}
        />
      );
    }
  }

  /**
   * No preload, so no local agent — the page is in an ordinary browser.
   *
   * **In a dev build that is a developer, not a user.** Serving the shell from
   * `npm run dev` and refreshing it in Chrome is how the UI gets worked on, and
   * stopping at "open me in the app" every reload made that impossible without a
   * query-string trick. The agent line still says the agent is unreachable, so
   * nothing is being pretended; the chat simply cannot talk to a local agent,
   * which is already true and already reported.
   *
   * The gate itself is unchanged for shipped builds, where the page *is* served
   * from a public hostname and the message is the whole answer.
   */
  const bridgeless = !window.bimgo;
  // **And on a loopback host** — see `isLocalHost`. A development-mode build
  // published to a real hostname would otherwise hand the whole application to
  // any browser that visited it, login page included.
  const devBrowser = import.meta.env.DEV && bridgeless && isLocalHost();

  /**
   * The recovery landing, **before** the desktop gate — the one route that is
   * meant to be opened in an ordinary browser (#21).
   *
   * A reset link is followed from an email client, in whatever browser the
   * machine defaults to, by someone who by definition cannot get into the app.
   * Answering "open me in the app" there is a dead end with no way out of it,
   * so this route is exempt and every other route is not.
   *
   * It sits above the `isConfigured()` gate as well, but is guarded by it: an
   * unconfigured build cannot construct a Supabase client, and the sentence that
   * gate prints is the more useful of the two.
   *
   * `/auth/confirm` is the step in front of it, not a second exemption: the
   * reset email lands there, and it turns into `/update-password` once the
   * token has been spent (`pages/AuthConfirm`).
   */
  if (resolveRoute(window.location.pathname) === "update-password" && isConfigured()) {
    return <UpdatePassword />;
  }
  if (window.location.pathname === AUTH_CONFIRM_PATH && isConfigured()) {
    return <AuthConfirm />;
  }

  // The desktop check comes first: without the bridge the page cannot reach a
  // local agent at all, and that is true of a configured build as much as an
  // unconfigured one. Saying "this build is not configured" to someone who
  // opened the URL in Chrome answers a question they did not ask.
  if (bridgeless && !devBrowser) {
    return (
      <Frame agent={agent}>
        <p>
          This page needs the Bimgo AI 2.0 app — the local agent is only reachable from
          there. Open it in the app rather than a browser.
        </p>
      </Frame>
    );
  }

  // An unconfigured build cannot even construct a Supabase client, and saying so
  // beats a network error that reads as "login is broken".
  if (!isConfigured()) {
    return (
      <Frame agent={agent}>
        <p>
          This build is not configured. Copy <code>src/web/.env.example</code> to{" "}
          <code>.env.local</code> and set <code>VITE_SUPABASE_URL</code>,{" "}
          <code>VITE_SUPABASE_ANON_KEY</code> and <code>VITE_API_BASE_URL</code>.
        </p>
        <Composer agent={agent} />
      </Frame>
    );
  }

  // Everything below asks the bridge a question, so it is skipped when there is
  // deliberately no bridge to ask.
  if (!devBrowser) {
    if (error) return <Frame agent={agent}><p>Bridge error: {error}</p></Frame>;
    if (!info) return <Frame agent={agent}><p>Connecting to the desktop…</p></Frame>;

    if (info.bridgeVersion < MIN_BRIDGE_VERSION) {
      return (
        <Frame agent={agent}>
          <p>
            This version of the desktop app is too old for the current web build
            (bridge v{info.bridgeVersion}, needs v{MIN_BRIDGE_VERSION}). Update the desktop app.
          </p>
        </Frame>
      );
    }
  }

  // `ready` is not the same as "signed out": rendering the login form before the
  // first getSession() resolves flashes it at an already-signed-in user on every
  // launch.
  if (!ready) return <Frame agent={agent}><p>Restoring session…</p></Frame>;
  if (!session) return <Login />;

  /**
   * The organization check, before the shell rather than inside it (#63).
   *
   * `AppShell` decides on every render whether the current org may be worked in
   * — see `orgGate.ts` — but it can only decide once `/organizations` has come
   * back, and it draws itself in the meantime. That meant a signed-in user whose
   * subscription had lapsed watched the chat appear and then be replaced by a
   * card telling them they could not use it. Blocking here instead is the same
   * argument `!ready` makes one line above: a screen that is about to be taken
   * away should not be shown in the first place.
   *
   * It is a `Frame` rather than a spinner in the shell because that is what this
   * chain is: the pre-shell gates all share one surface, so a user who is stuck
   * meets a finished page instead of a half-built application.
   */
  if (organizations.kind === "loading") {
    return <Frame agent={agent}><p>Checking your organization…</p></Frame>;
  }

  /**
   * A failed lookup is **not** an empty one. Falling through here would hand the
   * shell an empty list, which `orgGate` would read as "you belong to no
   * organization" — telling a user their membership is gone because a request
   * timed out. Distinguishing the two is the same discipline `OrgSwitcher`
   * already applies to its four states.
   */
  if (organizations.kind === "error") {
    return (
      <Frame agent={agent}>
        <p>Could not check your organization: {organizations.message}</p>
        <div className="gate-actions">
          <button type="button" className="gate-button" onClick={() => window.location.reload()}>
            Try again
          </button>
          <button type="button" className="gate-button" data-action="sign-out" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </Frame>
    );
  }

  /**
   * The block itself, and it takes the whole window (#63).
   *
   * It sits here rather than inside `AppShell` so that a blocked user sees the
   * page and *nothing else* — no sidebar, no nav, no chat behind it. A
   * gate rendered into the shell's content area still shows the application it
   * is refusing, which reads as a broken app rather than as an answer, and
   * leaves routes on screen that go nowhere.
   *
   * The cost is that the account menu's organization switcher goes with it, so
   * the page carries its own — see `OrgGateView`. That is the multi-org case:
   * being blocked on one organization must not strand someone who has another.
   */
  const gate = orgGate({
    loaded: organizations.kind === "loaded",
    organizations: organizations.kind === "loaded" ? organizations.organizations : [],
    currentOrgId: selectedOrgId,
  });
  if (gate.kind !== "ok") {
    return (
      <OrgGateView
        gate={gate}
        organizations={organizations.kind === "loaded" ? organizations.organizations : []}
        currentOrgId={selectedOrgId}
        onSelectOrg={setSelectedOrgId}
      />
    );
  }

  return (
    <AppShell
      session={session}
      agent={agent}
      info={info}
      plugins={plugins}
      hosts={hosts}
      organizations={organizations}
      selectedOrgId={selectedOrgId}
      onSelectOrg={setSelectedOrgId}
    />
  );
}

/**
 * The pre-shell frame, on the same surface as the login page — gradient
 * backdrop, brand lockup and a white card — so a user who cannot get past a
 * gate (opened in a browser, not configured, connecting, version too old) meets
 * a finished page rather than bare system text. It carries the same agent line
 * the shell's instance card does, so the status a user needs when they are stuck is
 * visible on the gate itself.
 */
function Frame({ agent, children }: { agent: AgentTarget; children: React.ReactNode }) {
  const colour = agent.ok
    ? "#2ea043"
    : agent.reason === "starting" || agent.reason === "resolving"
      ? "#d29922"
      : "#f85149";

  return (
    <main className="auth-page">
      <div className="auth-shell">
        <div className="auth-header">
          <img className="auth-logo" src="/bimgo-logo-horizontal-color.png" alt="Bimgo AI" />
        </div>
        <div className="auth-card">
          <h1 className="auth-title">Bimgo AI 2.0 (Beta)</h1>
          <div className="gate-body">{children}</div>
          <div className="auth-foot">
            <span className="status-dot" style={{ background: colour }} />
            <span data-status="agent">{describeAgent(agent)}</span>
          </div>
        </div>
      </div>
    </main>
  );
}
